import React, { useState } from "react";
import { Plus, Trash2, Star, Pencil, Save, X, MapPin } from "lucide-react";
import { shopApi, useShopAuth, formatShopAuthError } from "../../lib/shopAuth";
import { COLORS, Panel, GhostButton, SoftInput } from "./ui";

const EMPTY = { label: "", name: "", address: "", postal_code: "", city: "", phone: "", is_default: false };

const Field = ({ label, className = "", ...props }) => (
  <label className={`block ${className}`}>
    <span className="block text-[10px] uppercase tracking-[0.2em] mb-1.5" style={{ color: COLORS.secondary }}>{label}</span>
    <SoftInput {...props} />
  </label>
);

const AddressForm = ({ initial, onSave, onCancel, busy }) => {
  const [a, setA] = useState(initial);
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });
  const ok = a.address.trim() && a.postal_code.trim() && a.city.trim();
  return (
    <div className="grid grid-cols-6 gap-3 p-4 mt-3" style={{ border: `1px dashed ${COLORS.gold}` }} data-testid="address-form">
      <Field className="col-span-6 sm:col-span-2" label="Nom de l'adresse" placeholder="Maison, Bureau…" value={a.label} onChange={set("label")} data-testid="address-label" />
      <Field className="col-span-6 sm:col-span-4" label="Destinataire" value={a.name} onChange={set("name")} data-testid="address-name" />
      <Field className="col-span-6" label="Rue et numéro *" value={a.address} onChange={set("address")} data-testid="address-street" />
      <Field className="col-span-2" label="NPA *" value={a.postal_code} onChange={set("postal_code")} data-testid="address-postal" />
      <Field className="col-span-4" label="Localité *" value={a.city} onChange={set("city")} data-testid="address-city" />
      <Field className="col-span-6 sm:col-span-3" label="Téléphone" value={a.phone} onChange={set("phone")} data-testid="address-phone" />
      <div className="col-span-6 flex gap-2 justify-end">
        <GhostButton onClick={onCancel} icon={X} testid="address-cancel">Annuler</GhostButton>
        <GhostButton onClick={() => onSave(a)} loading={busy} disabled={!ok} icon={Save} testid="address-save">Enregistrer</GhostButton>
      </div>
    </div>
  );
};

const AddressCard = ({ a, onEdit, onDelete, onDefault }) => (
  <div className="p-4 flex items-start justify-between gap-3" style={{ border: `1px solid ${a.is_default ? COLORS.gold : "rgba(242,236,228,0.08)"}` }} data-testid={`address-card-${a.id}`}>
    <div className="text-sm" style={{ color: COLORS.primary }}>
      <p className="font-semibold flex items-center gap-2">
        <MapPin className="w-3.5 h-3.5" style={{ color: COLORS.gold }} /> {a.label || "Adresse"}
        {a.is_default && <span className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5" style={{ color: COLORS.gold, border: `1px solid ${COLORS.gold}` }} data-testid={`address-default-${a.id}`}>Par défaut</span>}
      </p>
      {a.name && <p style={{ color: COLORS.secondary }}>{a.name}</p>}
      <p style={{ color: COLORS.secondary }}>{a.address}, {a.postal_code} {a.city}</p>
      {a.phone && <p style={{ color: COLORS.muted }} className="text-xs">{a.phone}</p>}
    </div>
    <div className="flex gap-1 shrink-0">
      {!a.is_default && <button type="button" onClick={onDefault} title="Définir par défaut" className="p-2 hover:bg-white/5" data-testid={`address-make-default-${a.id}`}><Star className="w-4 h-4" style={{ color: COLORS.gold }} /></button>}
      <button type="button" onClick={onEdit} title="Modifier" className="p-2 hover:bg-white/5" data-testid={`address-edit-${a.id}`}><Pencil className="w-4 h-4" style={{ color: COLORS.secondary }} /></button>
      <button type="button" onClick={onDelete} title="Supprimer" className="p-2 hover:bg-white/5" data-testid={`address-delete-${a.id}`}><Trash2 className="w-4 h-4" style={{ color: COLORS.chili }} /></button>
    </div>
  </div>
);

export const AddressBook = () => {
  const { customer, refresh } = useShopAuth();
  const list = customer?.addresses || [];
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const persist = async (next) => {
    setBusy(true); setErr("");
    try { await shopApi.put("/auth/me/addresses", { addresses: next }); await refresh(); setEditing(null); }
    catch (e) { setErr(formatShopAuthError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const save = (a) => persist(editing === "new" ? [...list, a] : list.map((x) => (x.id === a.id ? a : x)));
  const makeDefault = (id) => persist(list.map((x) => ({ ...x, is_default: x.id === id })));

  return (
    <Panel testid="address-book" className="mt-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="text-lg" style={{ color: COLORS.primary, fontFamily: "Georgia, serif" }}>Mes adresses de livraison</h2>
        {editing === null && <GhostButton onClick={() => setEditing("new")} icon={Plus} testid="address-add">Ajouter une adresse</GhostButton>}
      </div>
      {err && <p className="text-xs mb-3" style={{ color: COLORS.chili }} data-testid="address-error">{err}</p>}
      {!list.length && editing === null && <p className="text-sm" style={{ color: COLORS.secondary }} data-testid="address-empty">Aucune adresse enregistrée. Ajoutez-en une pour la choisir en un clic lors de vos commandes.</p>}
      <div className="grid sm:grid-cols-2 gap-3">
        {list.map((a) => (editing === a.id ? null : (
          <AddressCard key={a.id} a={a} onEdit={() => setEditing(a.id)} onDefault={() => makeDefault(a.id)} onDelete={() => persist(list.filter((x) => x.id !== a.id))} />
        )))}
      </div>
      {editing !== null && (
        <AddressForm
          key={editing}
          initial={editing === "new" ? { ...EMPTY, is_default: !list.length } : list.find((x) => x.id === editing)}
          onSave={save}
          onCancel={() => setEditing(null)}
          busy={busy}
        />
      )}
    </Panel>
  );
};

export default AddressBook;
